import React, { useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CometChatIncomingCall,
  CometChatMessageComposer,
  CometChatMessageHeader,
  CometChatMessageList,
  CometChatUsers,
} from "@cometchat/chat-uikit-react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import "@cometchat/chat-uikit-react/css-variables.css";
import theme from "../../theme";
import UserNavbarComponent from "../../components/Navbar/UserNavbarComponent";
import {
  createCometChatUser,
  getLoggedInCometChatUser,
  initializeCometChat,
  isCometChatConfigured,
  loginCometChatUser,
  logoutCometChatUser,
  missingCometChatConfigMessage,
} from "../../service/CometChatService";
import { _DecryptService } from "../../service/EncryptDecryptService";
import _JWTDecodeService from "../../service/JWTDecodeService";

const ChatPage = ({
  history,
  roleFilter = "admin",
  forcedCometChatUid = "",
}) => {
  const [formState, setFormState] = useState({
    userId: forcedCometChatUid || "",
    displayName: "",
  });
  const [customerEmail, setCustomerEmail] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [activeUser, setActiveUser] = useState(null);
  const autoLoginAttempted = useRef(false);
  const usersRequestBuilder = useMemo(() => {
    if (!roleFilter) {
      return undefined;
    }
    try {
      return new CometChat.UsersRequestBuilder()
        .setRole(roleFilter)
        .setLimit(50);
    } catch (error) {
      console.error("Unable to prepare users request:", error);
      return undefined;
    }
  }, [roleFilter]);

  const getUidFromEmail = (email = "") =>
    email.split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "_");

  useEffect(() => {
    if (!isCometChatConfigured) {
      setInitError(missingCometChatConfigMessage);
      setIsInitializing(false);
      return;
    }

    const token = sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = _JWTDecodeService(_DecryptService(token));
        const decryptedEmail = decoded?.Email
          ? _DecryptService(decoded.Email)
          : "";
        const decryptedName = decoded?.UserName
          ? _DecryptService(decoded.UserName)
          : "";
        setCustomerEmail(decryptedEmail);
        setFormState((prev) => ({
          ...prev,
          userId:
            forcedCometChatUid || !decryptedEmail
              ? prev.userId
              : getUidFromEmail(decryptedEmail),
          displayName: decryptedName || prev.displayName,
        }));
      } catch (error) {
        console.error("Unable to read user info for chat:", error);
      }
    }

    initializeCometChat()
      .then(() => getLoggedInCometChatUser())
      .then((user) => {
        if (user) {
          setLoggedInUser(user);
        }
      })
      .catch((error) => {
        setInitError(
          error?.message || "Unable to initialize CometChat at the moment."
        );
      })
      .finally(() => setIsInitializing(false));
  }, [forcedCometChatUid]);

  useEffect(() => {
    if (!forcedCometChatUid) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      userId: forcedCometChatUid,
    }));
  }, [forcedCometChatUid]);

  const connectWithUid = async (
    uidToConnect,
    displayNameForCreation = ""
  ) => {
    const trimmedId = (uidToConnect || "").trim();

    if (!trimmedId) {
      setLoginError("Enter a CometChat user ID to continue.");
      return false;
    }

    setIsLoggingIn(true);

    try {
      const user = await loginCometChatUser(trimmedId);
      setLoggedInUser(user);
      setActiveUser(null);
      return true;
    } catch (error) {
      const trimmedDisplayName = (displayNameForCreation || "").trim();
      const isMissingUser =
        error?.code === "ERR_UID_NOT_FOUND" ||
        /uid[_-]?not[_-]?found/i.test(error?.message || "");

      if (trimmedDisplayName && isMissingUser) {
        try {
          await createCometChatUser({
            uid: trimmedId,
            name: trimmedDisplayName,
          });
          const user = await loginCometChatUser(trimmedId);
          setLoggedInUser(user);
          setActiveUser(null);
          return true;
        } catch (creationError) {
          setLoginError(
            creationError?.message ||
              "Unable to create a CometChat user. Verify your credentials and try again."
          );
          return false;
        }
      }

      if (isMissingUser && !trimmedDisplayName) {
        setLoginError(
          "This user does not exist yet. Add a display name so we can create it for you."
        );
        return false;
      }

      setLoginError(
        error?.message || "Unable to login to CometChat. Please try again."
      );
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogout = async () => {
    setLoginError("");
    try {
      await logoutCometChatUser();
    } catch (error) {
      setLoginError(
        error?.message || "Unable to logout from CometChat. Please try again."
      );
    } finally {
      setLoggedInUser(null);
      setActiveUser(null);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    await connectWithUid(formState.userId, formState.displayName);
  };

  useEffect(() => {
    if (
      !forcedCometChatUid ||
      isInitializing ||
      loggedInUser ||
      isLoggingIn
    ) {
      return;
    }

    if (autoLoginAttempted.current) {
      return;
    }

    autoLoginAttempted.current = true;
    setLoginError("");
    connectWithUid(
      forcedCometChatUid,
      formState.displayName || "Admin Chat"
    );
  }, [
    forcedCometChatUid,
    formState.displayName,
    isInitializing,
    isLoggingIn,
    loggedInUser,
  ]);

  const renderInitializationState = () => (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 420,
        margin: "0 auto",
        padding: 4,
        borderRadius: 3,
        textAlign: "center",
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="h6">Connecting to CometChat...</Typography>
        <Typography variant="body2" color="textSecondary">
          Please wait while we prepare the live chat experience.
        </Typography>
      </Stack>
    </Paper>
  );

  const renderLoginForm = () => (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 520,
        margin: "0 auto",
        padding: 4,
        borderRadius: 3,
      }}
    >
      <Stack spacing={3}>
        <div>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Join the Live Chat
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {forcedCometChatUid
              ? "You're on the admin console, which uses a dedicated support UID."
              : "Use your CometChat UID to connect with our support team. If the UID does not exist yet, add a display name and we will create it on the fly."}
          </Typography>
        </div>
        {initError && (
          <Alert severity="error" sx={{ alignItems: "flex-start" }}>
            {initError}
          </Alert>
        )}
        {loginError && (
          <Alert severity="error" sx={{ alignItems: "flex-start" }}>
            {loginError}
          </Alert>
        )}
        <Stack component="form" spacing={2} onSubmit={handleLogin}>
          {forcedCometChatUid ? (
            <TextField
              label="CometChat UID"
              value={forcedCometChatUid}
              disabled
              helperText="Admin chat responses are sent from this UID."
            />
          ) : (
            <TextField
              label="Customer Email (CometChat ID)"
              name="userId"
              value={customerEmail}
              placeholder="Your account email"
              disabled
              helperText={
                customerEmail
                  ? "Your CometChat ID is derived from this email."
                  : "Sign in to your store account to auto-fill this field."
              }
            />
          )}
          <TextField
            label="Display Name (used when creating a new user)"
            name="displayName"
            value={formState.displayName}
            onChange={handleChange}
            placeholder="Support Agent"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={isLoggingIn || Boolean(initError) || !formState.userId}
            sx={{
              backgroundColor: "#F98C6B",
              "&:hover": { backgroundColor: "#F67A56" },
            }}
          >
            {isLoggingIn ? "Connecting..." : "Connect"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderActiveChat = () => {
    const activeUserUid =
      activeUser?.uid || activeUser?.getUid?.() || activeUser?.id;
    const usersComponentProps = usersRequestBuilder
      ? { usersRequestBuilder }
      : {};

    return (
      <Stack spacing={3}>
        <Paper
          elevation={2}
          sx={{
            padding: 3,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <div>
            <Typography variant="subtitle1" fontWeight={600}>
              Signed in as {loggedInUser?.name || loggedInUser?.uid}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Select a contact to start chatting or accept incoming messages in
              real time.
            </Typography>
          </div>
          <Button variant="outlined" color="primary" onClick={handleLogout}>
            Logout
          </Button>
        </Paper>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              flex: { xs: "1 1 auto", md: "0 0 320px" },
              borderRadius: 3,
              padding: 2,
              minHeight: { xs: 360, md: "70vh" },
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              People & Conversations
            </Typography>
            <Box sx={{ height: { xs: 320, md: "calc(70vh - 56px)" } }}>
              <CometChatUsers
                onItemClick={(user) => {
                  setActiveUser(user);
                }}
                activeUser={activeUser || undefined}
                {...usersComponentProps}
                showScrollbar
              />
            </Box>
          </Paper>
          <Paper
            elevation={2}
            sx={{
              flex: 1,
              borderRadius: 3,
              padding: 2,
              minHeight: { xs: 360, md: "70vh" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeUser ? (
              <>
                <CometChatMessageHeader user={activeUser} />
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 200,
                    overflow: "hidden",
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <CometChatMessageList
                    key={activeUserUid || "active-user"}
                    user={activeUser}
                    showSmartReplies
                  />
                </Box>
                <CometChatMessageComposer
                  key={`composer-${activeUserUid || "active-user"}`}
                  user={activeUser}
                  placeholderText="Type your message..."
                />
              </>
            ) : (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1, textAlign: "center" }}
              >
                <Typography variant="h6">
                  Select a contact to start chatting.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  The full conversation view will appear here once you choose a
                  user from the list.
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
        <CometChatIncomingCall />
      </Stack>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <UserNavbarComponent history={history} />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          paddingTop: 4,
          paddingX: 2,
          paddingBottom: 6,
        }}
      >
        {isInitializing
          ? renderInitializationState()
          : loggedInUser
          ? renderActiveChat()
          : renderLoginForm()}
      </Box>
    </ThemeProvider>
  );
};

export default ChatPage;
